import { useCallback, useContext } from 'react';
import { nip19 } from 'nostr-tools';
import { useNavigate } from 'react-router-dom';
import { DesktopContext } from '@/desktop/DesktopContext';

/**
 * Returns handlers for navigating to Nostr profiles and events.
 *
 * When inside the desktop (DesktopProvider), all NIP-19 navigation is
 * intercepted and routed to the appropriate desktop window.
 * Outside the desktop (e.g. NIP19Page), falls back to React Router navigation.
 */
export function useNostrNavigate() {
  const navigate = useNavigate();
  // Safe — DesktopContext defaults to undefined, no throw outside desktop
  const desktop = useContext(DesktopContext);

  /** Open a profile by hex pubkey */
  const openProfile = useCallback((pubkey: string, e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (desktop) {
      desktop.openProfile(pubkey);
    } else {
      navigate(`/${nip19.npubEncode(pubkey)}`);
    }
  }, [desktop, navigate]);

  /** Open a thread/event by hex event ID */
  const openEventById = useCallback((id: string, authorHint?: string, e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (desktop) {
      desktop.openEventById(id, authorHint);
    } else {
      navigate(`/${nip19.neventEncode({ id, ...(authorHint ? { author: authorHint } : {}) })}`);
    }
  }, [desktop, navigate]);

  /**
   * Decode any NIP-19 identifier string and route it to the right desktop
   * window (profile, thread, naddr article). Falls back to navigate() outside
   * the desktop. Returns false if the identifier is unrecognised.
   */
  const openNip19 = useCallback((identifier: string, e?: React.MouseEvent): boolean => {
    e?.preventDefault();
    e?.stopPropagation();

    // Strip a leading "/" if the href was `/${nip19id}`
    const raw = identifier.startsWith('/') ? identifier.slice(1) : identifier;

    let decoded;
    try {
      decoded = nip19.decode(raw);
    } catch {
      return false;
    }

    if (!desktop) {
      navigate(`/${raw}`);
      return true;
    }

    switch (decoded.type) {
      case 'npub':
        desktop.openProfile(decoded.data as string);
        break;
      case 'nprofile':
        desktop.openProfile((decoded.data as { pubkey: string }).pubkey);
        break;
      case 'note':
        desktop.openEventById(decoded.data as string);
        break;
      case 'nevent': {
        const d = decoded.data as { id: string; author?: string };
        desktop.openEventById(d.id, d.author);
        break;
      }
      case 'naddr': {
        const { kind } = decoded.data as { kind: number; pubkey: string; identifier: string };
        if (kind === 30023) {
          // Long-form article — open Articles app
          desktop.openWindow('articles', 'Articles');
        } else {
          // Other addressable events — show in nsite browser as fallback
          desktop.openNsite(`https://njump.me/${raw}`);
        }
        break;
      }
      default:
        // Unknown — fall back to browser navigation
        navigate(`/${raw}`);
    }
    return true;
  }, [desktop, navigate]);

  /** Open any https:// URL in the nsite browser (or a new tab outside desktop) */
  const openUrl = useCallback((url: string, e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (desktop) {
      desktop.openNsite(url);
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, [desktop]);

  return { openProfile, openEventById, openNip19, openUrl };
}
